import React, { useState, useEffect, useCallback } from "react";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../../constants/ZoomAttendance.constants";
import { ExlyInput } from "common/form";

const isValidValue = (val) => val !== "" && val !== null && val !== undefined;

const extractRawValue = (val) =>
  typeof val === "object" ? val?.target?.value : val;

export const useRangeFilter = ({
  fromKey,
  toKey,
  filterValues = {},
  setFilters,
}) => {
  const [fromValue, setFromValue] = useState(
    filterValues[fromKey] ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.EMPTY_STRING
  );
  const [toValue, setToValue] = useState(
    filterValues[toKey] ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.EMPTY_STRING
  );

  useEffect(() => {
    setFromValue(
      filterValues[fromKey] ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.EMPTY_STRING
    );
    setToValue(
      filterValues[toKey] ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.EMPTY_STRING
    );
  }, [filterValues[fromKey], filterValues[toKey], fromKey, toKey]);

  const updateFilters = useCallback(
    (newFrom, newTo) => {
      const updatedFilters = { ...filterValues };

      if (isValidValue(newFrom)) {
        updatedFilters[fromKey] = Number(newFrom);
      } else {
        delete updatedFilters[fromKey];
      }

      if (isValidValue(newTo)) {
        updatedFilters[toKey] = Number(newTo);
      } else {
        delete updatedFilters[toKey];
      }

      setFilters(updatedFilters);
    },
    [filterValues, fromKey, toKey, setFilters]
  );

  const handleFromChange = useCallback(
    (val) => {
      const rawVal = extractRawValue(val);
      setFromValue(rawVal);
      updateFilters(rawVal, toValue);
    },
    [toValue, updateFilters]
  );

  const handleToChange = useCallback(
    (val) => {
      const rawVal = extractRawValue(val);
      setToValue(rawVal);
      updateFilters(fromValue, rawVal);
    },
    [fromValue, updateFilters]
  );

  return {
    fromValue,
    toValue,
    handleFromChange,
    handleToChange,
  };
};

export const RangeFilterInput = ({
  fromKey,
  toKey,
  setFilters,
  filterValues = {},
  min = 0,
  max,
  fromPlaceholder = ZOOM_ATTENDANCE_CONSTANTS.FILTERS.ZERO_PLACEHOLDER,
  toPlaceholder = ZOOM_ATTENDANCE_CONSTANTS.FILTERS.MAX_PLACEHOLDER,
  fromLabel = ZOOM_ATTENDANCE_CONSTANTS.FILTERS.FROM_LABEL,
  toLabel = ZOOM_ATTENDANCE_CONSTANTS.FILTERS.TO_LABEL,
}) => {
  const { fromValue, toValue, handleFromChange, handleToChange } =
    useRangeFilter({
      fromKey,
      toKey,
      filterValues,
      setFilters,
    });

  return (
    <div className="tw-flex tw-items-center tw-justify-evenly tw-gap-3 tw-w-full tw-px-2">
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <span className="tw-text-xs tw-font-semibold tw-text-gray-600">
          {fromLabel}:
        </span>
        <ExlyInput
          type="number"
          min={min}
          max={max}
          placeholder={fromPlaceholder}
          value={fromValue}
          onChange={handleFromChange}
          className="tw-w-16 tw-h-7 tw-px-2 tw-bg-gray-50 hover:tw-bg-white tw-border tw-border-solid tw-border-gray-300 focus:tw-border-indigo-500 tw-rounded tw-text-xs tw-text-gray-800 tw-outline-none focus:tw-outline-none tw-transition-colors"
        />
      </div>

      <div> - </div>

      <div className="tw-flex tw-items-center tw-gap-1.5">
        <span className="tw-text-xs tw-font-semibold tw-text-gray-600">
          {toLabel}:
        </span>
        <ExlyInput
          type="number"
          min={min}
          max={max}
          placeholder={toPlaceholder}
          value={toValue}
          onChange={handleToChange}
          className="tw-w-16 tw-h-7 tw-px-2 tw-bg-gray-50 hover:tw-bg-white tw-border tw-border-solid tw-border-gray-300 focus:tw-border-indigo-500 tw-rounded tw-text-xs tw-text-gray-800 tw-outline-none focus:tw-outline-none tw-transition-colors"
        />
      </div>
    </div>
  );
};
